import type { BlockConfiguration } from './block-configurations';

export enum EmailBlockType {
  // Content blocks
  Text = "text",
  Heading = "heading",
  Button = "button",
  Image = "image",
  Video = "video",
  Divider = "divider",
  Spacer = "spacer",
  Social = "social",
  Quote = "quote",
  Menu = "menu",

  // Layout blocks
  Section = "section",
  Column = "column",
  Group = "group",
  Hero = "hero",
  Footer = "footer",

  // Advanced blocks
  Html = "html",
  Accordion = "accordion",
  Carousel = "carousel",
}

/**
 * Email Block instance in the email document tree
 * 
 * The configuration property is a JSON string that should be parsed using
 * parseBlockConfiguration() to get a strongly-typed BlockConfiguration object.
 */
export interface EmailBlock {
  id: string;
  emailDocumentId: string;
  blockType: string; // e.g., "text", "image", "section", "column"
  configuration: string; // JSON string with block-specific settings
  parentBlockId?: string; // null/undefined for root blocks
  childBlocks: EmailBlock[]; // Nested children
  orderIndex: number; // Position among siblings
  createdAt: string;
  updatedAt: string;
}

/**
 * Parse a block's configuration JSON string into a strongly-typed configuration object
 * 
 * @param block The email block with a configuration JSON string
 * @returns Parsed and typed block configuration
 * 
 * @example
 * ```typescript
 * const config = parseBlockConfiguration(block);
 * if (isTextBlockConfiguration(config)) {
 *   console.log(config.typography.fontSize); // Full type safety and IntelliSense
 * }
 * ```
 */
export function parseBlockConfiguration(block: EmailBlock): BlockConfiguration {
  try {
    return JSON.parse(block.configuration) as BlockConfiguration;
  } catch (error) {
    console.error('Failed to parse block configuration:', error);
    // Return a minimal valid configuration as fallback
    return {
      $blockType: block.blockType,
    } as BlockConfiguration;
  }
}
